import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import RoleCompetenciesTable from 'modules/job_catalog/components/RoleCompetenciesTable'
import type { Role } from 'modules/job_catalog/hooks/useRole'
import { RoleTabs } from 'modules/job_catalog/pages/Role/RoleTabs'
import policyAuthorizations, {
  PolicyTarget,
} from 'modules/permissions/store/collections/policyAuthorizations'

interface OutletContext {
  role: Role
  canEditRole: boolean
  isLoading: boolean
  competenciesAvailable: boolean
  canManageCompetencies: boolean
}

function Component() {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { role, isLoading, canEditRole, competenciesAvailable, canManageCompetencies } =
    useOutletContext<OutletContext>()
  const rolePolicyTarget: PolicyTarget = { id: role.id, policyTargetClass: '::JobCatalog::Role' }
  const canEditJob = policyAuthorizations.for('job_catalog.edit_job', rolePolicyTarget)
  const { loading: fetchingPolicies } = useAsyncData(
    async () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.edit_job', [rolePolicyTarget]),
      ]),
    []
  )

  const onCreateCompetency = () => {
    track(trackingEvents.jobCatalog.jobRolePageCreateAndAssignCompetencyStart)
    navigate(
      navigation.employees.jobCatalog.role.competencies.createCompetency.fullPath({
        id: role.id,
      })
    )
  }

  const onAssignCompetencies = () => {
    track(trackingEvents.jobCatalog.jobRolePageAssignCompetencyStart)
    navigate(
      navigation.employees.jobCatalog.role.competencies.assignCompetencies.fullPath({
        id: role.id,
      })
    )
  }

  return (
    <AuthorizeUser
      authorizeFn={() => competenciesAvailable || canEditRole || isLoading || fetchingPolicies}
    >
      <Box width="full" gap="s24" alignItems="flexEnd">
        <RoleTabs roleId={role.id} />
        <>
          <Button.Split
            type="secondary"
            onClick={onAssignCompetencies}
            label={i18n.t('job_catalog.competencies.assign_competencies')}
            disabled={!canEditJob || !canManageCompetencies}
          >
            <Button.Split.Option
              onClick={onCreateCompetency}
              description={i18n.t('job_catalog.competencies.create_and_assign_description')}
              disabled={!canEditJob || !canManageCompetencies}
            >
              {i18n.t('job_catalog.competencies.create_and_assign_title')}
            </Button.Split.Option>
          </Button.Split>
        </>
        <RoleCompetenciesTable roleId={role.id} canManageCompetencies={canManageCompetencies} />
      </Box>

      <Outlet context={{ role }} />
    </AuthorizeUser>
  )
}

export default Component
